import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import React, { useContext, useEffect } from 'react';
import { SearchContext } from '../../contexts/SearchContext';
import {
    IMaterial,
    ISearchResult
} from '../../interfaces';
import { MaterialDetailsDialog } from '../MaterialDetailsDialog';
import './index.scss';


export interface IResultMaterialsTable extends ISearchResult {
    isMaterialOut: boolean
}

export const ResultMaterialsTable: React.SFC<IResultMaterialsTable> = ({
    isMaterialOut,
    materialGroup,
    ...props
}: IResultMaterialsTable) => {

    const searchContext = useContext(SearchContext);

    const handleReload = (materialId?: string) => {
        if (materialId) {
            searchContext.reloadMaterial(materialId)
        }
    }

    const handleUpdated = (refetch: boolean) => {
        if (refetch) {
            searchContext.search();
        }
    }

    const showProperty = (propertyName: string) => {
        return searchContext.resultOptions
            .filter(x => x.propertyName === propertyName)[0]
            ?.visible;
    }

    const cellStyle = (material: IMaterial) => {
        return {
            color: material.overdue === true ? "#d32f2f" : "inherit",
            fontWeight: material.overdue === true ? "bold" : ""
        }
    }

    return (
        <Grid item xs={12}>
            <Table className="result-details-table">
                <TableHead>
                    <TableRow>
                        {showProperty("porterbrookAssetNumber") && <TableCell>Porterbrook Asset Number</TableCell>}
                        {showProperty("manufacturerNumber") && <TableCell>Manufacturer Number</TableCell>}
                        {showProperty("serialNumber") && <TableCell>Serial Number</TableCell>}
                        {showProperty("vehicleNumber") && <TableCell>Vehicle Number</TableCell>}
                        {showProperty("unitNumber") && <TableCell>Unit Number</TableCell>}
                        {showProperty("weightEstimate") && <TableCell>Weight Estimate</TableCell>}
                        {showProperty("valueEstimate") && <TableCell>Value Estimate</TableCell>}
                        {showProperty("totalValueEstimate") && <TableCell>Value Estimate (Total)</TableCell>}
                        {showProperty("storageRequestReference") && <TableCell>Storage Ref</TableCell>}
                        {showProperty("paperWorkReceived") && <TableCell>Paperwork Ref</TableCell>}
                        {showProperty("arrivalDate") && <TableCell>Arrival Date</TableCell>}
                        {showProperty("partExpiry") && <TableCell>Part Expiry</TableCell>}
                        {showProperty("pallet") && <TableCell>Pallet Number</TableCell>}
                        {showProperty("warehouse") && <TableCell>Warehouse</TableCell>}
                        {showProperty("rack") && <TableCell>Rack</TableCell>}
                        {showProperty("row") && <TableCell>Row</TableCell>}
                        {showProperty("notes") && <TableCell>Notes</TableCell>}

                        {showProperty("storageReason") && <TableCell>Storage Reason</TableCell>}
                        {showProperty("retentionDecision") && <TableCell>Retention Decision</TableCell>}
                        {showProperty("responsibleEngineerAtLastAudit") && <TableCell>Responsible Engineer</TableCell>}


                        {showProperty("quantity") && (isMaterialOut ? <><TableCell>Dispatched</TableCell><TableCell>Outstanding</TableCell></> : <TableCell>Qty</TableCell>)}
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {materialGroup.materials?.map(x => (
                        <Tooltip
                            key={x.materialId}
                            title={(x.overdue === true && x.expectedReturnDate) ? "Expected return by " + new Date(x.expectedReturnDate).toLocaleDateString("en-GB") : undefined}>
                            <TableRow key={x.materialId} >
                                {showProperty("porterbrookAssetNumber") && <TableCell sx={cellStyle(x)}>{x.porterbrookAssetNumber ? x.porterbrookAssetNumber : "-"}</TableCell>}
                                {showProperty("manufacturerNumber") && <TableCell sx={cellStyle(x)}>{x.manufacturerNumber ? x.manufacturerNumber : "-"}</TableCell>}
                                {showProperty("serialNumber") && <TableCell sx={cellStyle(x)}>{x.serialNumber ? x.serialNumber : "-"}</TableCell>}
                                {showProperty("vehicleNumber") && <TableCell sx={cellStyle(x)}>{x.vehicleNumber ? x.vehicleNumber : "-"}</TableCell>}
                                {showProperty("unitNumber") && <TableCell sx={cellStyle(x)}>{x.unitNumber ? x.unitNumber : "-"}</TableCell>}
                                {showProperty("weightEstimate") && <TableCell sx={cellStyle(x)}>{x.weightEstimate ? x.weightEstimate + "kg" : "-"}</TableCell>}
                                {showProperty("valueEstimate") && <TableCell sx={cellStyle(x)}>{x.valueEstimate ? x.valueEstimate : "-"}</TableCell>}
                                {showProperty("totalValueEstimate") && <TableCell sx={cellStyle(x)}>{x.totalValueEstimate ? x.totalValueEstimate : "-"}</TableCell>}
                                {showProperty("storageRequestReference") && <TableCell sx={cellStyle(x)}>{x.storageRequestReference ? x.storageRequestReference : "-"}</TableCell>}
                                {showProperty("paperWorkReceived") && <TableCell sx={cellStyle(x)}>{x.paperWorkReceived ? x.paperWorkReceived : "-"}</TableCell>}
                                {showProperty("arrivalDate") && <TableCell sx={cellStyle(x)}>{x.arrivalDate ? new Date(x.arrivalDate).toLocaleDateString("en-GB") : "-"}</TableCell>}
                                {showProperty("partExpiry") && <TableCell sx={cellStyle(x)}>{x.partExpiry ? new Date(x.partExpiry).toLocaleDateString("en-GB") : "-"}</TableCell>}
                                {showProperty("pallet") && <TableCell sx={cellStyle(x)}>{x.pallet ? x.pallet : "-"}</TableCell>}
                                {showProperty("warehouse") && <TableCell sx={cellStyle(x)}>{x.warehouse ? x.warehouse.value : "-"}</TableCell>}
                                {showProperty("rack") && <TableCell sx={cellStyle(x)}>{x.rack ? x.rack : "-"}</TableCell>}
                                {showProperty("row") && <TableCell sx={cellStyle(x)}>{x.row ? x.row : "-"}</TableCell>}
                                {showProperty("notes") && <TableCell sx={cellStyle(x)}>{(x.notes && x.notes.length > 0) ? "Y" : "N"}</TableCell>}
                                {showProperty("storageReason") && <TableCell sx={cellStyle(x)}>{x.storageReason ? x.storageReason : "-"}</TableCell>}
                                {showProperty("retentionDecision") && <TableCell sx={cellStyle(x)}>{x.retentionDecision ? x.retentionDecision : "-"}</TableCell>}
                                {showProperty("responsibleEngineerAtLastAudit") && <TableCell sx={cellStyle(x)}>{x.responsibleEngineerAtLastAudit ? x.responsibleEngineerAtLastAudit : "-"}</TableCell>}
                                {showProperty("quantity") && isMaterialOut && <TableCell sx={cellStyle(x)}>{x.quantityDispatched ? x.quantityDispatched : "0"} </TableCell>}
                                {showProperty("quantity") && <TableCell sx={cellStyle(x)}>{x.quantity ? x.quantity : "0"} <small>({x.unitOfQuantity.value})</small></TableCell>}
                                <TableCell sx={{ textAlign: "right" }}>
                                    <MaterialDetailsDialog
                                        material={x}
                                        viewMode="search_result"
                                        onReloadMaterial={() => handleReload(x.materialId)}
                                        onClose={handleUpdated}
                                    />
                                </TableCell>
                            </TableRow>
                        </Tooltip>
                    ))}
                </TableBody>

            </Table>
        </Grid>
    );
}